import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Avatar,
  Paper,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  Alert,
  AlertTitle,
  LinearProgress,
  Fade,
  Container,
  IconButton
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Description as DocumentIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

const PublicEmployeeDocuments = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [error, setError] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [docExists, setDocExists] = useState({}); // map key -> boolean
  const [gcsItems, setGcsItems] = useState([]);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [parsedEmployeeId, setParsedEmployeeId] = useState(null);
  const [linkStatus, setLinkStatus] = useState('checking'); // checking | valid | expired | invalid

  // Modal state for custom filename and multiple files
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDoc, setModalDoc] = useState(null); // {name, key, required}
  // modalFiles: [{file: File|null, filename: string}]
  const [modalFiles, setModalFiles] = useState([{ file: null, filename: '' }]);
  const [modalUploading, setModalUploading] = useState(false);
  const handleOpenModal = async (doc) => {
    // open modal and proactively fetch GCS listing for this employee so the modal can show existing files
    try {
      setModalDoc(doc);
      setModalFiles([{ file: null, filename: '' }]);
      setModalOpen(true);
      // fetch and update gcsItems (fetchGcsList sets state internally)
      if (employee) {
        await fetchGcsList(employee);
      }
    } catch (err) {
      console.error('Error fetching GCS list on modal open:', err);
      // still open the modal even if list-gcs fails
    }
  };
  const handleCloseModal = () => {
    setModalOpen(false);
    setModalDoc(null);
    setModalFiles([{ file: null, filename: '' }]);
    setModalUploading(false);
  };
  const handleModalFileChange = (idx, file) => {
    setModalFiles(prev => {
      const arr = [...prev];
      arr[idx] = { ...arr[idx], file };
      return arr;
    });
  };
  const handleModalFilenameChange = (idx, value) => {
    setModalFiles(prev => {
      const arr = [...prev];
      arr[idx] = { ...arr[idx], filename: value };
      return arr;
    });
  };
  const handleAddModalFile = () => {
    setModalFiles(prev => [...prev, { file: null, filename: '' }]);
  };
  const handleRemoveModalFile = (idx) => {
    setModalFiles(prev => prev.filter((_, i) => i !== idx));
  };
  const handleModalUpload = async () => {
    // Validate: all filename filled and file selected
    if (!modalFiles.every(f => f.file && f.filename.trim())) return;
    setModalUploading(true);
    for (let i = 0; i < modalFiles.length; i++) {
      const { file, filename } = modalFiles[i];
      if (!file || !filename.trim()) continue;
      await handleFileUpload(filename.trim(), modalDoc.key, file);
    }
    setModalUploading(false);
    handleCloseModal();
  };

  const documentCategories = [
    { name: 'Formulir Biodata Pelamar Kerja', key: 'biodata_pelamar' },
    { name: 'Surat Lamaran (Wajib Tulis Tangan)', key: 'surat_lamaran', required: true },
    { name: 'Upload CV', key: 'upload_cv', required: false },
    { name: 'FC KTP', key: 'fc_ktp', required: true },
    { name: 'FC SIM (Yang Masih Berlaku)', key: 'fc_sim', required: true },
    { name: 'Pas Foto 4X6 Berwarna Merah', key: 'pas_foto', required: true },
    { name: 'Hasil Test MTK', key: 'hasil_test_mtk', required: true },
    { name: 'FC BPJS Kesehatan', key: 'bpjs_kesehatan', required: false },
    { name: 'FC BPJS Ketenagakerjaan', key: 'bpjs_ketenagakerjaan', required: false },
    { name: 'Sertifikat Vaksin 1 & 2 (Jika sudah vaksin 3 mohon disertakan)', key: 'vaksin', required: false },
    { name: 'FC NPWP', key: 'fc_npwp', required: true },
    { name: 'FC Rekening Mandiri (Wajib)', key: 'fc_rekening_mandiri', required: true },
    { name: 'FC Akte Lahir', key: 'fc_akte_lahir', required: true },
    { name: 'FC Akte Nikah (Jika Sudah Menikah)', key: 'fc_akte_nikah', required: false },
    { name: 'Surat Kesehatan (Wajib)', key: 'surat_kesehatan', required: true },
    { name: 'FC Ijazah (Wajib)', key: 'fc_ijazah', required: true },
    { name: 'Transkrip Nilai (Wajib)', key: 'transkrip_nilai', required: true },
    { name: 'FC Sertifikat - Sertifikat (Jika Memiliki)', key: 'fc_sertifikat', required: false },
    { name: 'Surat Keterangan Kerja (Jika Memiliki)', key: 'surat_kerja', required: false },
    { name: 'FC SKCK (Wajib)', key: 'fc_skck', required: true },
    { name: 'Surat Keterangan', key: 'surat_keterangan', required: false },
    { name: 'Surat Jaminan', key: 'surat_jaminan', required: false },
    { name: 'FC KTP Ayah', key: 'fc_ktp_ayah', required: true },
    { name: 'FC Kartu Keluarga', key: 'fc_kartu_keluarga', required: true },
    { name: 'Foto Full Body', key: 'foto_full_body', required: true },
    { name: 'FC KTP Penjamin', key: 'fc_ktp_penjamin', required: true },
    { name: 'Hasil Tes DISC', key: 'disc_test', required: true, link: 'https://tes.anthonykusuma.com/disc/tes' },
    { name: 'Dan Lain Lain', key: 'dan_lain_lain', required: false }
  ];

  useEffect(() => {
    fetchEmployeeData();
  }, [token]);

  // Parse composite token format: <employeeId>_<timestamp>_<random>
  const parseCompositeToken = (raw) => {
    if (!raw) return null;
    const parts = raw.split('_');
    if (parts.length < 3) return null;
    const [empId, tsStr] = parts;
    if (!/^[a-fA-F0-9]{24}$/.test(empId)) return null;
    const timestamp = Number(tsStr);
    if (!Number.isFinite(timestamp)) return null;
    return { empId, timestamp };
  };

  // expiry for employee public tokens: 72 hours (3 days)
  const EXPIRY_MS = 72 * 60 * 60 * 1000;

  const fetchEmployeeData = async () => {
    try {
      // Validate token from localStorage
      const tokenDataStr = localStorage.getItem(`public_token_${token}`);
      let employeeIdForFetch = null;
      if (tokenDataStr) {
        // Local storage path (same browser)
        const tokenData = JSON.parse(tokenDataStr);
        const expiryDate = new Date(tokenData.expiresAt);
        const now = new Date();
        if (now > expiryDate) {
          setTokenExpired(true);
          setError('Link sudah kadaluarsa (berlaku 72 jam). Silakan minta link baru dari HRD.');
          setLinkStatus('expired');
          setLoading(false);
          return;
        }
        employeeIdForFetch = tokenData.employeeId;
        setLinkStatus('valid');
      } else {
        // Fallback: parse composite token so link works cross-browser
        const parsed = parseCompositeToken(token);
        if (!parsed) {
          setTokenExpired(true);
          setError('Format link tidak valid. Minta link baru dari HRD.');
          setLinkStatus('invalid');
          setLoading(false);
          return;
        }
        const age = Date.now() - parsed.timestamp;
        if (age > EXPIRY_MS) {
          setTokenExpired(true);
          setError('Link sudah kadaluarsa (berlaku 72 jam). Silakan minta link baru dari HRD.');
          setLinkStatus('expired');
          setLoading(false);
          return;
        }
        employeeIdForFetch = parsed.empId;
        setLinkStatus('valid');
      }
      setParsedEmployeeId(employeeIdForFetch);

      // Fetch employee data by ID (public read)
      const response = await fetch(`https://apidashboard.eurekagroup.id/employee/get/${employeeIdForFetch}`);

      if (!response.ok) {
        throw new Error('Failed to fetch employee data');
      }

      const result = await response.json();
      setEmployee(result);
      setError(null);
      // fetch GCS listing for this employee then compute existence
      try {
        const items = await fetchGcsList(result);
        await checkAllDocsExistence(result, items);
      } catch (e) {
        // fallback to previous per-doc probe
        setTimeout(() => {
          checkAllDocsExistence(result);
        }, 200);
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Gagal memuat data. Silakan coba lagi atau hubungi HRD.');
    } finally {
      setLoading(false);
    }
  };

  // Check if a document exists on backend GCS using view-gcs endpoint.
  // Check existence of documents by calling list-gcs and matching names
  const checkGcsExists = async (employeeObj, documentLabel) => {
    if (!employeeObj) return false;
    try {
      const resp = await fetch('https://apidashboard.eurekagroup.id/documents/list-gcs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emp_id: employeeObj.emp_id || '', full_name: employeeObj.full_name || '' })
      });
      if (!resp.ok) return false;
      const json = await resp.json();
      if (!Array.isArray(json)) return false;
      const sanitized = documentLabel.replace(/\([^)]*\)/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
      return json.some(item => {
        if (!item) return false;
        if (typeof item === 'string') return item.toLowerCase().includes(sanitized) || item.toLowerCase() === sanitized;
        const name = (item.document_name || item.name || item.filename || item.key || '').toString().toLowerCase();
        return name.includes(sanitized) || name === sanitized;
      });
    } catch (err) {
      return false;
    }
  };

  // Check existence using list-gcs items if provided, otherwise fallback to per-doc probe
  const checkAllDocsExistence = async (employeeObj, items = null) => {
    if (!employeeObj) return;
    const map = {};
    // use provided items (from list-gcs) if available
    const list = Array.isArray(items) ? items : gcsItems;
    documentCategories.forEach((doc) => {
      try {
        const sanitized = doc.name.replace(/\([^)]*\)/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
        let exists = false;
        if (Array.isArray(list) && list.length > 0) {
          exists = list.some(item => {
            const filename = (item.filename || item.name || item.document_name || '').toString().toLowerCase();
            return filename.includes(sanitized) || filename === sanitized;
          });
        }
        // fallback: if employee.documents has matching category key
        if (!exists && employeeObj.documents && Array.isArray(employeeObj.documents)) {
          exists = employeeObj.documents.some(d => d.category_name === doc.key || (d.document_name || '').toLowerCase().includes(sanitized));
        }
        map[doc.key] = !!exists;
      } catch (e) {
        map[doc.key] = false;
      }
    });
    setDocExists(map);
  };

  // Fetch list of files from backend list-gcs for public page
  const fetchGcsList = async (employeeObj) => {
    if (!employeeObj) return [];
    try {
      const resp = await fetch('https://apidashboard.eurekagroup.id/documents/list-gcs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emp_id: employeeObj.emp_id || '', full_name: employeeObj.full_name || '' })
      });
      if (!resp.ok) {
        console.error('list-gcs failed', resp.status);
        setGcsItems([]);
        return [];
      }
      const json = await resp.json();
      const items = Array.isArray(json.items) ? json.items : (Array.isArray(json) ? json : []);
      // dedupe by filename (case-insensitive)
      const seen = new Set();
      const unique = [];
      items.forEach(it => {
        const filename = (it && (it.filename || it.name || it.document_name) || '').toString().trim();
        const key = filename.toLowerCase();
        if (filename && !seen.has(key)) {
          seen.add(key);
          unique.push(it);
        }
      });
      setGcsItems(unique);
      return unique;
    } catch (error) {
      console.error('Error fetching list-gcs:', error);
      setGcsItems([]);
      return [];
    }
  };

  // Open document helper: prefer public link -> direct http gsUri -> download endpoint
  const openDocument = (employeeObj, docObj, documentLabel) => {
    const sanitizedLabel = (documentLabel || '').replace(/\([^)]*\)/g, '').replace(/\s+/g, ' ').trim();
    const publicLink = docObj && docObj.document_code ? `https://dashboard.eurekagroup.id/public/document/${docObj.document_code}` : null;
    const directUrl = docObj && docObj.gsUri && typeof docObj.gsUri === 'string' && (docObj.gsUri.startsWith('http') || docObj.gsUri.startsWith('https')) ? docObj.gsUri : null;
    const fallback = buildGcsDownloadUrl(employeeObj, sanitizedLabel);
    const url = publicLink || directUrl || fallback;
    if (url && url !== '#') window.open(url, '_blank');
  };

  // Helper untuk build URL view & download dari backend GCS (public)
  const buildGcsViewUrl = (employee, documentLabel) => {
    if (!employee) return '#';
    const nama = encodeURIComponent(employee.full_name || '');
    const nim = encodeURIComponent(employee.emp_id || parsedEmployeeId || '');
    const sanitizedLabel = documentLabel
      .replace(/\([^)]*\)/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    const document_type = encodeURIComponent(sanitizedLabel);
    return `https://apidashboard.eurekagroup.id/documents/view-gcs?nama=${nama}&nim=${nim}&document_type=${document_type}`;
  };

  const buildGcsDownloadUrl = (employee, documentLabel) => {
    if (!employee) return '#';
    const nama = encodeURIComponent(employee.full_name || '');
    const nim = encodeURIComponent(employee.emp_id || parsedEmployeeId || '');
    const sanitizedLabel = documentLabel
      .replace(/\([^)]*\)/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    const document_type = encodeURIComponent(sanitizedLabel);
    return `https://apidashboard.eurekagroup.id/documents/download-gcs?nama=${nama}&nim=${nim}&document_type=${document_type}`;
  };

  const handleFileUpload = async (documentName, categoryKey, file) => {
    if (!file || !employee) return;

    setUploadingDoc(documentName);
    setUploadProgress(prev => ({ ...prev, [categoryKey]: 0 }));

    // Ekstensi dari file asli (default pdf)
    const originalExt = file.name.includes('.')
      ? file.name.split('.').pop()
      : (file.type === 'application/pdf' ? 'pdf' : 'pdf');

    // Bersihkan nama dokumen (hapus teks dalam kurung, rapikan spasi)
    const sanitizedLabel = documentName
      .replace(/\([^)]*\)/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    // Untuk "Dan Lain Lain", gunakan nama custom dari user
    // Untuk dokumen lainnya, gunakan nama kategori dokumen
    const docCategory = documentCategories.find(d => d.key === categoryKey);
    const isCustomDoc = categoryKey === 'dan_lain_lain';

    // Jika "Dan Lain Lain", pakai documentName yang diisi user
    // Jika dokumen kategori lain, pakai nama kategori
    const finalDocumentName = isCustomDoc ? sanitizedLabel : (docCategory?.name || sanitizedLabel);
    const finalSanitizedLabel = finalDocumentName
      .replace(/\([^)]*\)/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    // Contoh: "FC KTP.pdf" atau "lamaran yoyoy.pdf" (untuk Dan Lain Lain)
    const backendFileName = `${isCustomDoc ? sanitizedLabel : finalSanitizedLabel}.${originalExt}`;

    // Folder: <FullName>-<EmpId> (fallback parsedEmployeeId kalau emp_id kosong)
    const folder = `${employee.full_name || 'Unknown'}-${employee.emp_id || parsedEmployeeId || 'Unknown'}`;

    const gsUri = `gs://hris-eureka-documents/${folder}/${backendFileName}`;

    // === Di bawah ini: FormData, tapi isi sama kaya JSON contoh ===
    const formData = new FormData();

    // File binary langsung dipakai (nama file asli dari user)
    formData.append('file', file);

    // nama: nama employee
    formData.append('nama', employee.full_name || '');

    // nim: gunakan emp_id kalau ada, fallback ke parsedEmployeeId
    formData.append('nim', employee.emp_id || parsedEmployeeId || '');

    // document_type: label dokumen yang sudah dibersihkan
    formData.append('document_type', finalSanitizedLabel);

    // Field2 metadata yang backend kamu mau, konsepnya sama dengan JSON
    formData.append('filename', backendFileName);     // "FC KTP.pdf" atau "lamaran yoyoy.pdf"
    formData.append('folder', folder);                // "Budi kuprit-P3101"
    formData.append('gsUri', gsUri);                  // "gs://hris-eureka-documents/..."

    // Kalau backend masih butuh field lama, boleh tetap kirim:
    formData.append('id_user', employee._id);

    try {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(prev => ({ ...prev, [categoryKey]: percentComplete }));
        }
      });

      xhr.addEventListener('load', () => {
        let responseJson = null;
        try {
          responseJson = JSON.parse(xhr.responseText || '{}');
        } catch (e) {
          responseJson = null;
        }

        const isSuccessStatus = xhr.status === 200 || xhr.status === 201;
        const isSuccessFlag = responseJson && responseJson.success === true;

        if (isSuccessStatus && isSuccessFlag) {
          // Notif sukses yang modern - selalu di depan modal lain
          Swal.fire({
            icon: 'success',
            title: 'Upload Berhasil!',
            html: `
            <div style="text-align:left;font-size:0.9rem;">
              <p><strong>Dokumen:</strong> ${responseJson.filename || backendFileName}</p>
              <p><strong>Folder:</strong> ${responseJson.folder || folder}</p>
              <p><strong>Lokasi:</strong><br/><code style="font-size:0.75rem;">${responseJson.gsUri || gsUri}</code></p>
            </div>
          `,
            showConfirmButton: false,
            timer: 2500,
            timerProgressBar: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false,
            heightAuto: false,
            backdrop: `
            rgba(0,0,0,0.6)
            left top
            no-repeat
          `,
            didOpen: () => {
              const popup = Swal.getPopup();
              if (popup) {
                popup.style.zIndex = '99999';
              }
              const container = Swal.getContainer();
              if (container) {
                container.style.zIndex = '99998';
              }
            }
          });

          setUploadProgress(prev => ({ ...prev, [categoryKey]: 100 }));
          setTimeout(() => {
            setUploadProgress(prev => {
              const newProgress = { ...prev };
              delete newProgress[categoryKey];
              return newProgress;
            });
            fetchEmployeeData(); // Refresh data
          }, 1000);
        } else {
          // Notif gagal dengan detail dari backend jika ada
          let msg = 'Gagal mengunggah dokumen. Silakan coba lagi.';

          if (responseJson) {
            if (responseJson.message) {
              msg = responseJson.message;
            } else if (responseJson.error) {
              // contoh: {"error":"Only PDF or JPG/JPEG allowed"}
              msg = responseJson.error;
            } else if (typeof responseJson === 'string') {
              msg = responseJson;
            }
          }

          if (responseJson?.error === 'Only PDF or JPG/JPEG allowed') {
            msg = 'Tipe file tidak diperbolehkan. Hanya boleh PDF atau JPG/JPEG.';
          }
          Swal.fire({
            icon: 'error',
            title: 'Upload Gagal',
            text: msg,
            confirmButtonText: 'Tutup',
            confirmButtonColor: '#d32f2f',
            allowOutsideClick: false,
            allowEscapeKey: false,
            heightAuto: false,
            backdrop: 'rgba(0,0,0,0.6)',
            didOpen: () => {
              const popup = Swal.getPopup();
              if (popup) {
                popup.style.zIndex = '99999';
              }
              const container = Swal.getContainer();
              if (container) {
                container.style.zIndex = '99998';
              }
            }
          });
        }

        setUploadingDoc(null);
      });

      xhr.addEventListener('error', () => {
        Swal.fire({
          icon: 'error',
          title: 'Upload Gagal',
          text: 'Terjadi kesalahan jaringan. Silakan coba lagi.',
          confirmButtonText: 'Tutup',
          confirmButtonColor: '#d32f2f',
          allowOutsideClick: false,
          allowEscapeKey: false,
          heightAuto: false,
          backdrop: 'rgba(0,0,0,0.6)',
          didOpen: () => {
            const popup = Swal.getPopup();
            if (popup) {
              popup.style.zIndex = '99999';
            }
            const container = Swal.getContainer();
            if (container) {
              container.style.zIndex = '99998';
            }
          }
        });
        setUploadingDoc(null);
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[categoryKey];
          return newProgress;
        });
      });

      xhr.open('POST', 'https://apidashboard.eurekagroup.id/documents/upload');
      xhr.send(formData);
    } catch (error) {
      console.error('Error uploading document:', error);
      setError('Gagal mengunggah dokumen. Silakan coba lagi.');
      setUploadingDoc(null);
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[categoryKey];
        return newProgress;
      });
    }
  };

  const getUploadedCount = () => {
    // Count uploaded documents by documentCategories: prefer docExists (GCS probe)
    // but also consider backend employee.documents fuzzy matches via isDocumentUploaded.
    try {
      if (!Array.isArray(documentCategories)) return 0;
      return documentCategories.reduce((acc, doc) => {
        const key = doc.key;
        const existsInMap = !!(docExists && docExists[key]);
        const existsInEmployee = isDocumentUploaded(key);
        return acc + (existsInMap || existsInEmployee ? 1 : 0);
      }, 0);
    } catch (e) {
      // fallback to backend documents length
      if (!employee?.documents) return 0;
      return employee.documents.length;
    }
  };

  const getRequiredCount = () => {
    return documentCategories.filter(doc => doc.required).length;
  };

  // Normalize helper for fuzzy matching
  const normalize = (str) => (str || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const isDocumentUploaded = (categoryKey) => {
    if (!employee?.documents) return false;
    // Direct match (legacy)
    const direct = employee.documents.some(doc => doc.category_name === categoryKey || doc.id_document_category === categoryKey);
    if (direct) return true;
    // Fuzzy match: at least 2 token overlaps between backend category_name and frontend key/name
    const targetTokens = normalize(categoryKey).split(' ').filter(Boolean);
    return employee.documents.some(doc => {
      const catTokens = normalize(doc.category_name).split(' ').filter(Boolean);
      const nameTokens = normalize(doc.document_name).split(' ').filter(Boolean);
      const combined = new Set([...catTokens, ...nameTokens]);
      let hits = 0;
      targetTokens.forEach(t => { if (combined.has(t)) hits++; });
      return hits >= Math.min(2, targetTokens.length); // require 2 unless less than 2 tokens
    });
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#2886df',
        gap: 3
      }}>
        <CircularProgress size={60} sx={{ color: 'white' }} />
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
          Memuat data dokumen...
        </Typography>
      </Box>
    );
  }

  if ((tokenExpired || error) && linkStatus !== 'valid') {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: '#2886df',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3
      }}>
        <Container maxWidth="sm">
          <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Avatar sx={{
                width: 80,
                height: 80,
                bgcolor: 'error.main',
                margin: '0 auto 20px'
              }}>
                <WarningIcon sx={{ fontSize: 40 }} />
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: 'error.main' }}>
                {linkStatus === 'expired' ? 'Link Kadaluarsa' : 'Link Tidak Valid'}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                {error}
              </Typography>
              <Alert severity="info">
                <AlertTitle>Cara Mendapatkan Link Baru</AlertTitle>
                Hubungi HRD untuk mendapatkan link baru yang berlaku 72 jam sejak dibuat.
              </Alert>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  const uploadedCount = getUploadedCount();
  const requiredCount = getRequiredCount();
  const totalCount = documentCategories.length;
  const progressPercentage = (uploadedCount / totalCount) * 100;

  return (
    <Box sx={{
      minHeight: '100vh',
      background: '#2886df',
      py: 4
    }}>
      {/* Modal for custom filename and multiple files */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, bgcolor: '#2886df', color: 'white', pb: 2 }}>
          Upload Dokumen: {modalDoc?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                {modalFiles.map((item, idx) => {
                  // Check if file exists in gcsItems
                  let uploaded = false;
                  let viewUrl = null;
                  let downloadUrl = null;
                  if (Array.isArray(gcsItems) && item.filename.trim()) {
                    const match = gcsItems.find(g => (g.filename || '').toLowerCase() === `${item.filename.trim().toLowerCase()}.pdf`);
                    if (match) {
                      uploaded = true;
                      viewUrl = match.viewUrl;
                      downloadUrl = match.downloadUrl;
                    }
                  }
                  return (
                    <Grid item xs={12} sm={6} md={3} key={idx}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField
                          label={`Nama File ${idx + 1}`}
                          variant="outlined"
                          value={item.filename}
                          onChange={e => handleModalFilenameChange(idx, e.target.value)}
                          sx={{ bgcolor: '#f5faff', borderRadius: 2, width: '100%', fontSize: '0.8rem' }}
                          inputProps={{ maxLength: 60, style: { fontSize: '0.8rem', padding: 8 } }}
                          InputLabelProps={{ style: { fontSize: '0.75rem' } }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {uploaded && viewUrl ? (
                            <Button
                              variant="contained"
                              color="success"
                              sx={{ borderRadius: 2, fontWeight: 600, flex: 1, fontSize: '0.8rem', py: 1, minHeight: 32 }}
                              onClick={() => window.open(viewUrl, '_blank')}
                            >
                              Lihat File
                            </Button>
                          ) : (
                            <Button
                              component="label"
                              variant="contained"
                              color="primary"
                              startIcon={<UploadIcon />}
                              sx={{ borderRadius: 2, fontWeight: 600, flex: 1, fontSize: '0.8rem', py: 1, minHeight: 32 }}
                            >
                              <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                {item.file ? item.file.name : `Pilih File ${idx + 1}`}
                              </span>
                              <input
                                type="file"
                                accept="image/*,.pdf"
                                hidden
                                onChange={e => handleModalFileChange(idx, e.target.files[0])}
                              />
                            </Button>
                          )}
                          {modalFiles.length > 1 && (
                            <IconButton color="error" onClick={() => handleRemoveModalFile(idx)}>
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                        {uploaded && (
                          <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                            Sudah diupload
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              sx={{ borderRadius: 2, fontWeight: 600, alignSelf: 'flex-start', mt: 2 }}
              onClick={handleAddModalFile}
              disabled={modalFiles.length >= 51}
            >
              Tambah File
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseModal} color="inherit" sx={{ borderRadius: 2, fontWeight: 600 }}>
            Batal
          </Button>
          <Button
            onClick={handleModalUpload}
            color="primary"
            variant="contained"
            sx={{ borderRadius: 2, fontWeight: 600 }}
            disabled={modalUploading || !modalFiles.every(f => f.file && f.filename.trim())}
            startIcon={modalUploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
          >
            {modalUploading ? 'Uploading...' : 'Upload Document'}
          </Button>
        </DialogActions>
      </Dialog>
      <Container maxWidth="lg">
        {/* Header */}
        <Card sx={{
          borderRadius: 3,
          mb: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          color: 'white'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
              <Avatar sx={{
                width: 80,
                height: 80,
                bgcolor: 'rgba(255,255,255,0.2)',
                fontSize: '2rem',
                fontWeight: 700
              }}>
                {employee?.full_name?.charAt(0)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  📄 Upload Dokumen Karyawan
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.95, mb: 0.5 }}>
                  {employee?.full_name} ({employee?.emp_id})
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<TimeIcon />}
                    label="Link berlaku 72 jam"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                  <Chip
                    icon={<DocumentIcon />}
                    label={`${uploadedCount} / ${totalCount} Dokumen`}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Progress Bar */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Progress Upload Dokumen
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {Math.round(progressPercentage)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progressPercentage}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: 'white',
                    borderRadius: 5
                  }
                }}
              />
            </Box>

            <Alert severity="warning" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', border: 'none' }}>
              <AlertTitle sx={{ color: 'white', fontWeight: 700 }}>📌 Perhatian</AlertTitle>
              Silakan upload semua dokumen yang diperlukan. Dokumen dengan tanda <strong>(*)</strong> wajib diupload.
              Format file yang diterima: <strong>PDF, JPG, JPEG, PNG</strong>
            </Alert>
          </CardContent>
        </Card>

        {/* Documents Grid */}
        <Grid container spacing={3}>
          {documentCategories.map((doc, index) => {
            // consider GCS existence first (checked after employee load), fallback to fuzzy matching
            const isUploaded = (docExists && docExists[doc.key]) || isDocumentUploaded(doc.key);
            const progress = uploadProgress[doc.key];
            const isUploading = uploadingDoc === doc.name;

            return (
              <Grid item xs={12} sm={6} md={4} key={doc.key}>
                <Fade in timeout={300 + index * 50}>
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: isUploaded ? 'success.main' : doc.required ? 'error.main' : 'grey.300',
                      background: isUploaded
                        ? 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)'
                        : 'white',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6
                      }
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: isUploaded ? 'success.main' : doc.required ? 'error.main' : 'grey.400',
                            width: 45,
                            height: 45
                          }}
                        >
                          {isUploaded ? <CheckIcon /> : <DocumentIcon />}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              color: isUploaded ? 'success.main' : 'text.primary',
                              mb: 0.5
                            }}
                          >
                            {doc.name} {doc.required && <span style={{ color: '#f44336' }}>*</span>}
                          </Typography>
                          {doc.required && !isUploaded && (
                            <Chip
                              label="Wajib"
                              size="small"
                              color="error"
                              sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                            />
                          )}
                          {isUploaded && (
                            <Chip
                              label="✓ Sudah Upload"
                              size="small"
                              color="success"
                              sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                            />
                          )}
                        </Box>
                      </Box>

                      {doc.link && !isUploaded && (
                        <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                          <Typography variant="caption">
                            <a href={doc.link} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', fontWeight: 600 }}>
                              Klik di sini untuk tes DISC
                            </a>
                          </Typography>
                        </Alert>
                      )}

                      {/* If already uploaded: show View + Perbarui Doc; otherwise Upload */}
                      {isUploaded ? (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              // prefer list-gcs returned viewUrl/downloadUrl if available
                              const sanitized = doc.name.replace(/\([^)]*\)/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
                              const match = (gcsItems || []).find(item => {
                                const filename = (item.filename || item.name || item.document_name || '').toString().toLowerCase();
                                return filename.includes(sanitized) || filename === sanitized;
                              });
                              if (match && match.viewUrl) {
                                window.open(match.viewUrl, '_blank');
                              } else if (match && match.downloadUrl) {
                                window.open(match.downloadUrl, '_blank');
                              } else {
                                // fallback to download endpoint by name
                                const fallback = buildGcsDownloadUrl(employee, doc.name);
                                if (fallback && fallback !== '#') window.open(fallback, '_blank');
                              }
                            }}
                            sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.8rem', px: 1.5 }}
                          >
                            View
                          </Button>
                          {/* Perbarui: directly pick file and upload (no modal) */}
                          <label style={{ width: '100%' }}>
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              hidden
                              onChange={(e) => {
                                const f = e.target.files && e.target.files[0];
                                if (f) handleFileUpload(doc.name, doc.key, f);
                              }}
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<UploadIcon />}
                              component="span"
                              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, width: '100%' }}
                              disabled={isUploading}
                            >
                              {isUploading ? 'Uploading...' : 'Perbarui Doc'}
                            </Button>
                          </label>
                        </Box>
                      ) : doc.key === 'dan_lain_lain' ? (
                        <Button
                          variant={isUploaded ? 'outlined' : 'contained'}
                          size="small"
                          startIcon={<UploadIcon />}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            width: '100%',
                            bgcolor: isUploaded ? 'background.paper' : undefined
                          }}
                          color={isUploaded ? 'success' : (doc.required ? 'error' : 'primary')}
                          onClick={() => handleOpenModal(doc)}
                          disabled={isUploading}
                        >
                          {isUploading ? 'Uploading...' : 'Upload Dokumen'}
                        </Button>
                      ) : (
                        <label style={{ width: '100%' }}>
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            hidden
                            onChange={(e) => {
                              const f = e.target.files && e.target.files[0];
                              if (f) handleFileUpload(doc.name, doc.key, f);
                            }}
                          />
                          <Button
                            variant={isUploaded ? 'outlined' : 'contained'}
                            size="small"
                            startIcon={<UploadIcon />}
                            component="span"
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600,
                              width: '100%',
                              bgcolor: isUploaded ? 'background.paper' : undefined
                            }}
                            color={isUploaded ? 'success' : (doc.required ? 'error' : 'primary')}
                            disabled={isUploading}
                          >
                            {isUploading ? 'Uploading...' : 'Upload Dokumen'}
                          </Button>
                        </label>
                      )
                      }

                      {progress !== undefined && (
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            {progress}%
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            );
          })}
        </Grid>

        {/* Footer */}
        <Card sx={{
          borderRadius: 3,
          mt: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
        }}>
          <CardContent sx={{ textAlign: 'center', p: 3 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Jika mengalami kesulitan atau ada pertanyaan, silakan hubungi HRD
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
              © 2025 Eureka Group. All rights reserved.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default PublicEmployeeDocuments;
